import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ConfirmationPopup = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title = "Confirmation", 
  message = "Are you sure you want to do this?", 
  confirmText = "Confirm", 
  cancelText = "Cancel",
  confirmButtonClass = "bg-red-600 hover:bg-red-700",
  cancelButtonClass = "bg-gray-700 hover:bg-gray-600",
  icon = "warning"
}) => {
  const [isRendered, setIsRendered] = useState(false);
  

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';

      setTimeout(() => setIsRendered(true), 10);
    } else {
      document.body.style.overflow = 'auto';
      setIsRendered(false);
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const getIcon = () => {
    switch (icon) {
      case 'warning':
        return (
          <svg className="w-16 h-16 mx-auto text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      case 'info':
        return (
          <svg className="w-16 h-16 mx-auto text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'success':
        return (
          <svg className="w-16 h-16 mx-auto text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'question':
        return (
          <svg className="w-16 h-16 mx-auto text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-16 h-16 mx-auto text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
    }
  };

  // Background animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  // Modal animation variants
  const modalVariants = {
    hidden: { 
      scale: 0.8,
      opacity: 0,
      y: 20
    },
    visible: { 
      scale: 1,
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 500
      }
    },
    exit: { 
      scale: 0.8,
      opacity: 0,
      y: 20,
      transition: {
        duration: 0.2
      }
    }
  };

  // Content animation variants
  const contentVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        delay: 0.1,
        duration: 0.4
      }
    }
  };
  
  // Button hover animation
  const buttonHoverAnimation = {
    scale: 1.05,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 10
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden"
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={backdropVariants}
        >
          {/* Backdrop with blur effect */}
          <motion.div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          
          {/* Popup card */}
          <motion.div 
            className="relative z-10 w-full max-w-md mx-4 overflow-hidden rounded-2xl"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Visual purple glow effect */}
            <div className="absolute inset-0 bg-black rounded-2xl">
              <div className="absolute inset-0 opacity-80 bg-gradient-to-br from-slate-900 to-black rounded-2xl"></div>
              <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_center,rgba(124,58,237,0.8)_0%,transparent_70%)]"></div>
            </div>
            
            {/* Content container */}
            <div className="relative p-6 border border-purple-500/20 rounded-2xl">
              <motion.div 
                className="text-center"
                variants={contentVariants}
              >
                {getIcon()}
                
                <h2 className="mt-4 text-xl font-bold text-slate-200">{title}</h2>
                <p className="mt-2 text-slate-300">{message}</p>
                
                <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
                  <motion.button
                    whileHover={buttonHoverAnimation}
                    whileTap={{ scale: 0.95 }}
                    onClick={onClose}
                    className={`px-6 py-3 text-white rounded-lg ${cancelButtonClass} transition-colors duration-200`}
                  >
                    {cancelText}
                  </motion.button>
                  
                  <motion.button
                    whileHover={buttonHoverAnimation}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      onConfirm();
                      onClose();
                    }}
                    className={`px-6 py-3 text-white rounded-lg ${confirmButtonClass} transition-colors duration-200`}
                  >
                    {confirmText}
                  </motion.button>
                </div>
              </motion.div>
              
              {/* Optional decorative elements */}
              <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-purple-500/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-purple-500/20 to-transparent"></div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConfirmationPopup;